import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import AssetModuleNavbar from "./AssetModuleNavbar";
import API_BASE_URL from "../config";
import SystemNotes from "./SystemNotes";

/* ── Collapsible Section ── */
const Section = ({ title, children }) => {
  const [open, setOpen] = useState(true);
  return (
    <div className="mb-3 border">
      <div
        className="fw-bold p-2 border-bottom d-flex justify-content-between align-items-center"
        style={{ cursor: "pointer", backgroundColor: "#DFE4EB" }}
        onClick={() => setOpen(!open)}
      >
        <span>{title}</span>
        <span>{open ? "▾" : "▸"}</span>
      </div>
      {open && <div className="row g-3 p-3">{children}</div>}
    </div>
  );
};

const dropStyle = {
  position: "absolute", top: "100%", left: 0, right: 0, zIndex: 9999,
  background: "#fff", border: "1px solid #dee2e6", borderRadius: "0 0 6px 6px",
  maxHeight: 220, overflowY: "auto", boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
};

const STATUS_OPTIONS = ["OPEN", "IN_PROGRESS", "COMPLETED", "CANCELLED"];
const STATUS_BADGE = {
  OPEN:        { bg: "#fff3cd", color: "#856404", label: "Open" },
  IN_PROGRESS: { bg: "#cfe2ff", color: "#084298", label: "In Progress" },
  COMPLETED:   { bg: "#d1e7dd", color: "#0a3622", label: "Completed" },
  CANCELLED:   { bg: "#f8d7da", color: "#842029", label: "Cancelled" },
};

export default function EditMaintenance() {
  const navigate   = useNavigate();
  const { id }     = useParams();
  const token      = localStorage.getItem("token");

  /* ── Form state ── */
  const [formData, setFormData] = useState({
    maintenanceType: "BREAKDOWN",
    serviceDate: "",
    issueDescription: "",
    workDone: "",
    partsReplaced: "",
    repairCost: "",
    vendorName: "",
    slaTarget: 3,
    status: "OPEN",
    completionDate: "",
    remarks: "",
  });

  /* ── UI state ── */
  const [loading,    setLoading]    = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [originalRecord, setOriginalRecord] = useState(null);

  /* ── Asset search ── */
  const [allAssets,     setAllAssets]     = useState([]);
  const [assetSearch,   setAssetSearch]   = useState("");
  const [filteredAssets,setFilteredAssets]= useState([]);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [showAssetDrop, setShowAssetDrop] = useState(false);
  const assetRef = useRef(null);

  /* ── Vendor search ── */
  const [allVendors,     setAllVendors]     = useState([]);
  const [vendorSearch,   setVendorSearch]   = useState("");
  const [filteredVendors,setFilteredVendors]= useState([]);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [showVendorDrop, setShowVendorDrop] = useState(false);
  const vendorRef = useRef(null);

  /* ── Tabs ── */
  const [activeTab, setActiveTab] = useState("systemNotes");
  const [visitedTabs, setVisitedTabs] = useState(new Set(["systemNotes"]));
  const [navbarColor, setNavbarColor] = useState("#080235");

  /* ── Bootstrap CSS ── */
  useEffect(() => {
    const bl = document.createElement("link"); bl.rel = "stylesheet"; bl.href = "https://cdn.jsdelivr.net/npm/bootstrap@5.3.2/dist/css/bootstrap.min.css"; document.head.appendChild(bl);
    const ic = document.createElement("link"); ic.rel = "stylesheet"; ic.href = "https://cdn.jsdelivr.net/npm/bootstrap-icons@1.11.1/font/bootstrap-icons.css"; document.head.appendChild(ic);
    return () => { bl.remove(); ic.remove(); };
  }, []);

  /* ── Auth guard ── */
  useEffect(() => {
    const roles = JSON.parse(localStorage.getItem("roles") || "[]");
    if (!token)                        { toast.error("You must be logged in."); navigate("/"); return; }
    // if (!roles.includes("ROLE_ADMIN")) { toast.error("Unauthorized.");          navigate("/"); }
  }, [navigate, token]);

  /* ── Navbar Color ── */
  useEffect(() => {
      const userId = localStorage.getItem("userId");
      if (!token || !userId) return;
      fetch(`${API_BASE_URL}/api/appearance/${userId}`, { headers: { Authorization: `Bearer ${token}` } })
          .then(r => r.json()).then(d => { if (d?.navbarColor) setNavbarColor(d.navbarColor); }).catch(() => { });
  }, [token]);

  /* ── Load assets & vendors ── */
  useEffect(() => {
    if (!token) return;
    fetch(`${API_BASE_URL}/api/assets?page=0&size=10000`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => { const l = Array.isArray(d) ? d : d.content || []; setAllAssets(l); setFilteredAssets(l); })
      .catch(() => {});

    fetch(`${API_BASE_URL}/api/vendors`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => { const l = Array.isArray(d) ? d : []; setAllVendors(l); setFilteredVendors(l); })
      .catch(() => {});
  }, [token]);

  /* ── Load maintenance record ── */
  useEffect(() => {
    if (!token || !id) return;
    setLoading(true);
    fetch(`${API_BASE_URL}/api/asset-maintenance/${id}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => { if (!r.ok) throw new Error("Record not found"); return r.json(); })
      .then(data => {
        setOriginalRecord(data);

        /* Populate form fields from the loaded record */
        setFormData({
          maintenanceType:  data.maintenanceType  || "BREAKDOWN",
          serviceDate:      data.serviceDate      || "",
          issueDescription: data.issueDescription || "",
          workDone:         data.workDone         || "",
          partsReplaced:    data.partsReplaced     || "",
          repairCost:       data.repairCost        ?? "",
          vendorName:       data.vendorName        || "",
          slaTarget:        data.slaTarget         ?? 3,
          status:           data.status            || "OPEN",
          completionDate:   data.completionDate    || "",
          remarks:          data.remarks           || "",
        });

        /* Pre-select the asset */
        if (data.assetId || data.asset) {
          const assetId   = data.assetId   || data.asset?.id;
          const assetCode = data.assetCode || data.asset?.assetCode || "";
          const assetName = data.assetName || data.asset?.assetName || "";
          setSelectedAsset({ id: assetId, assetCode, assetName, ...data.asset });
          setAssetSearch(`${assetCode} – ${assetName}`);
        }

        /* Pre-select the vendor */
        if (data.vendorName) {
          setVendorSearch(data.vendorName);
        }
      })
      .catch(err => { toast.error(err.message || "Failed to load record."); navigate("/allmaintenance"); })
      .finally(() => setLoading(false));
  }, [id, token, navigate]);

  /* ── Asset filter ── */
  useEffect(() => {
    const q = assetSearch.trim().toLowerCase();
    if (!q) { setFilteredAssets(allAssets); return; }
    setFilteredAssets(allAssets.filter(a =>
      (a.assetName    || "").toLowerCase().includes(q) ||
      (a.assetCode    || "").toLowerCase().includes(q) ||
      (a.serialNumber || "").toLowerCase().includes(q) ||
      (a.brand        || "").toLowerCase().includes(q)
    ));
  }, [assetSearch, allAssets]);

  /* ── Vendor filter ── */
  useEffect(() => {
    const q = vendorSearch.trim().toLowerCase();
    if (!q) { setFilteredVendors(allVendors); return; }
    setFilteredVendors(allVendors.filter(v =>
      (v.name          || "").toLowerCase().includes(q) ||
      (v.contactPerson || "").toLowerCase().includes(q) ||
      (v.phone         || "").toLowerCase().includes(q)
    ));
  }, [vendorSearch, allVendors]);

  /* ── Click-outside to close dropdowns ── */
  useEffect(() => {
    const handler = e => {
      if (assetRef.current  && !assetRef.current.contains(e.target))  setShowAssetDrop(false);
      if (vendorRef.current && !vendorRef.current.contains(e.target)) setShowVendorDrop(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleChange = e => setFormData(p => ({ ...p, [e.target.name]: e.target.value }));

  /* ── Auto set completion date when status becomes COMPLETED ── */
  const handleStatusChange = e => {
    const status = e.target.value;
    setFormData(p => ({
      ...p,
      status,
      completionDate: status === "COMPLETED" && !p.completionDate
        ? new Date().toISOString().split("T")[0]
        : p.completionDate,
    }));
  };

  /* ── Submit PUT ── */
  const handleSubmit = async e => {
    e.preventDefault();
    if (!selectedAsset) { toast.error("Please select an asset."); return; }

    setSubmitting(true);
    try {
      const body = {
        assetId: selectedAsset.id,
        ...formData,
        repairCost: formData.repairCost === "" ? null : Number(formData.repairCost),
        slaTarget:  Number(formData.slaTarget),
      };

      const res = await fetch(`${API_BASE_URL}/api/asset-maintenance/${id}`, {
        method:  "PUT",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body:    JSON.stringify(body),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || "Failed to update maintenance record.");
      }

      toast.success("Maintenance record updated successfully.");
      setTimeout(() => navigate("/allmaintenance"), 1200);
    } catch (err) {
      toast.error(err.message || "Update failed.");
    } finally {
      setSubmitting(false);
    }
  };

  /* ── Status badge pill ── */
  const StatusPill = ({ status }) => {
    const s = STATUS_BADGE[status] || { bg: "#e9ecef", color: "#495057", label: status };
    return (
      <span style={{
        display: "inline-block", padding: "3px 12px", borderRadius: 20,
        fontSize: 12, fontWeight: 600, background: s.bg, color: s.color,
      }}>{s.label}</span>
    );
  };

  /* ═══════════ RENDER ═══════════ */
  if (loading) {
    return (
      <>
        <div className="fixed-top"><AssetModuleNavbar /></div>
        <div className="container-fluid" style={{ paddingTop: 120 }}>
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status" />
            <div className="text-muted mt-3">Loading maintenance record…</div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <ToastContainer position="top-right" autoClose={4000} />
      <div className="fixed-top"><AssetModuleNavbar /></div>
      <div className="container-fluid my-4" style={{ paddingTop: "90px" }}>

        {/* ── Page Header ── */}
        <div className="d-flex justify-content-between align-items-start mb-3">
          <div>
            <h4 className="fw-bold mb-0">
              <i className="bi bi-pencil-square me-2 text-warning"></i>
              Edit Maintenance Record
            </h4>
            <div className="d-flex gap-2 mt-3  align-items-center">
            <button className="btn btn-primary" type="submit" form="edit-maintenance-form" disabled={submitting}
              style={{ minWidth: 50 }}>
              {submitting
                ? <><span className="spinner-border spinner-border-sm me-1" /> Saving…</>
                : <> Save</>}
            </button>
            <button className="btn btn-secondary btn-sm" type="button" onClick={() => navigate(-1)}>
              Cancel
            </button>
           
          </div>
          </div>
          
        </div>

        {/* ── Change summary banner ── */}
        {originalRecord?.status && (
          <div className="alert alert-warning d-flex align-items-center gap-2 py-2 mb-3" style={{ fontSize: 13 }}>
            <i className="bi bi-exclamation-triangle-fill text-warning"></i>
            <span>
              You are editing a <strong>{originalRecord.maintenanceType}</strong> maintenance record.
              {originalRecord.status === "COMPLETED" && (
                <span className="ms-1 text-danger fw-semibold">This record is already marked Completed — changes will be saved.</span>
              )}
            </span>
          </div>
        )}

        <form id="edit-maintenance-form" onSubmit={handleSubmit}>

          {/* ── SECTION 1: Asset ── */}
          <Section title="Asset">
            <div className="col-md-5">
              <label className="form-label fw-semibold">
                Asset <span className="text-danger">*</span>
              </label>
              <div className="position-relative" ref={assetRef}>
                <div className="input-group">
                  <span className="input-group-text"><i className="bi bi-laptop" /></span>
                  <input
                    className="form-control"
                    placeholder="Search by code, name, brand…"
                    value={assetSearch}
                    onChange={e => { setAssetSearch(e.target.value); setSelectedAsset(null); setShowAssetDrop(true); }}
                    onFocus={() => setShowAssetDrop(true)}
                    autoComplete="off"
                  />
                  {selectedAsset && (
                    <button type="button" className="btn btn-outline-secondary"
                      onClick={() => { setSelectedAsset(null); setAssetSearch(""); }}>
                      <i className="bi bi-x" />
                    </button>
                  )}
                </div>
                {showAssetDrop && filteredAssets.length > 0 && (
                  <div style={dropStyle}>
                    {filteredAssets.slice(0, 30).map(a => (
                      <div key={a.id}
                        style={{ padding: "9px 14px", cursor: "pointer", fontSize: 13, borderBottom: "1px solid #f0f0f0" }}
                        onMouseEnter={e => e.currentTarget.style.background = "#f8f9fa"}
                        onMouseLeave={e => e.currentTarget.style.background = "#fff"}
                        onMouseDown={() => { setSelectedAsset(a); setAssetSearch(`${a.assetCode} – ${a.assetName}`); setShowAssetDrop(false); }}>
                        <span className="fw-semibold text-primary" style={{ fontFamily: "monospace" }}>{a.assetCode}</span>
                        <span className="ms-2">{a.assetName}</span>
                        <span className="ms-2 text-muted" style={{ fontSize: 11 }}>{a.category}{a.brand ? ` · ${a.brand}` : ""}</span>
                        <span className={`badge ms-2 ${a.status === "Available" ? "bg-success" : a.status === "In Repair" ? "bg-warning text-dark" : "bg-secondary"}`} style={{ fontSize: 10 }}>
                          {a.status}
                        </span>
                      </div>
                    ))}
                    {filteredAssets.length > 30 && (
                      <div className="text-muted text-center py-2" style={{ fontSize: 12 }}>
                        + {filteredAssets.length - 30} more — type to narrow
                      </div>
                    )}
                  </div>
                )}
                {showAssetDrop && filteredAssets.length === 0 && assetSearch && (
                  <div style={dropStyle}>
                    <div className="text-muted text-center py-3" style={{ fontSize: 13 }}>No assets found</div>
                  </div>
                )}
              </div>
            </div>

            {selectedAsset && (
              <>
                <div className="col-md-2">
                  <label className="form-label">Current Status</label>
                  <input className="form-control bg-light" readOnly value={selectedAsset.status || "—"} />
                </div>
                <div className="col-md-2">
                  <label className="form-label">Category</label>
                  <input className="form-control bg-light" readOnly value={selectedAsset.category || "—"} />
                </div>
                <div className="col-md-3">
                  <label className="form-label">Location</label>
                  <input className="form-control bg-light" readOnly value={selectedAsset.location || "—"} />
                </div>
              </>
            )}
          </Section>

          {/* ── SECTION 2: Maintenance Details ── */}
          <Section title="Maintenance Details">
            <div className="col-md-3">
              <label className="form-label fw-semibold">Maintenance Type</label>
              <select name="maintenanceType" className="form-select" value={formData.maintenanceType} onChange={handleChange}>
                <option value="BREAKDOWN">BREAKDOWN</option>
                <option value="PREVENTIVE">PREVENTIVE</option>
              </select>
            </div>

            <div className="col-md-3">
              <label className="form-label fw-semibold">Service Date</label>
              <input type="date" name="serviceDate" className="form-control" value={formData.serviceDate} onChange={handleChange} />
            </div>

            <div className="col-md-4">
              <label className="form-label fw-semibold">Vendor</label>
              <div className="position-relative" ref={vendorRef}>
                <div className="input-group">
                  <span className="input-group-text"><i className="bi bi-shop" /></span>
                  <input
                    className="form-control"
                    placeholder="Search vendor by name or contact…"
                    value={vendorSearch}
                    onChange={e => {
                      setVendorSearch(e.target.value);
                      setSelectedVendor(null);
                      setFormData(p => ({ ...p, vendorName: e.target.value }));
                      setShowVendorDrop(true);
                    }}
                    onFocus={() => setShowVendorDrop(true)}
                    autoComplete="off"
                  />
                  {selectedVendor && (
                    <button type="button" className="btn btn-outline-secondary"
                      onClick={() => { setSelectedVendor(null); setVendorSearch(""); setFormData(p => ({ ...p, vendorName: "" })); }}>
                      <i className="bi bi-x" />
                    </button>
                  )}
                </div>
                {showVendorDrop && filteredVendors.length > 0 && (
                  <div style={dropStyle}>
                    {filteredVendors.slice(0, 20).map(v => (
                      <div key={v.id}
                        style={{ padding: "9px 14px", cursor: "pointer", fontSize: 13, borderBottom: "1px solid #f0f0f0" }}
                        onMouseEnter={e => e.currentTarget.style.background = "#f8f9fa"}
                        onMouseLeave={e => e.currentTarget.style.background = "#fff"}
                        onMouseDown={() => {
                          setSelectedVendor(v);
                          setVendorSearch(v.name);
                          setFormData(p => ({ ...p, vendorName: v.name }));
                          setShowVendorDrop(false);
                        }}>
                        <span className="fw-semibold">{v.name}</span>
                        {v.contactPerson && <span className="ms-2 text-muted" style={{ fontSize: 12 }}>· {v.contactPerson}</span>}
                        {v.phone         && <span className="ms-2 text-muted" style={{ fontSize: 12 }}>· {v.phone}</span>}
                      </div>
                    ))}
                  </div>
                )}
                {showVendorDrop && filteredVendors.length === 0 && (
                  <div style={dropStyle}>
                    <div className="text-muted text-center py-3" style={{ fontSize: 13 }}>
                      No vendors found — you can still type a name manually
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="col-md-2">
              <label className="form-label fw-semibold">SLA Target (days)</label>
              <input type="number" name="slaTarget" className="form-control" value={formData.slaTarget} onChange={handleChange} min="1" />
            </div>

            <div className="col-md-12">
              <label className="form-label fw-semibold">Issue Description</label>
              <textarea name="issueDescription" className="form-control" rows={3}
                value={formData.issueDescription} onChange={handleChange}
                placeholder="Describe the problem in detail…" />
            </div>

            <div className="col-md-6">
              <label className="form-label fw-semibold">Work Done</label>
              <textarea name="workDone" className="form-control" rows={3}
                value={formData.workDone} onChange={handleChange}
                placeholder="Describe what was done to fix the issue…" />
            </div>

            <div className="col-md-3">
              <label className="form-label fw-semibold">Parts Replaced</label>
              <input type="text" name="partsReplaced" className="form-control"
                value={formData.partsReplaced} onChange={handleChange}
                placeholder="e.g. Battery, RAM" />
            </div>

            <div className="col-md-3">
              <label className="form-label fw-semibold">Repair Cost (₹)</label>
              <input type="number" name="repairCost" className="form-control"
                value={formData.repairCost} onChange={handleChange}
                min="0" placeholder="0" />
            </div>
          </Section>

          {/* ── SECTION 3: Status & Closure (edit-only section) ── */}
          <Section title="Status & Closure">
            <div className="col-md-3">
              <label className="form-label fw-semibold">Status</label>
              <select name="status" className="form-select" value={formData.status} onChange={handleStatusChange}>
                {STATUS_OPTIONS.map(s => (
                  <option key={s} value={s}>{STATUS_BADGE[s]?.label || s}</option>
                ))}
              </select>
              <div className="mt-2">
                <StatusPill status={formData.status} />
              </div>
            </div>

            <div className="col-md-3">
              <label className="form-label fw-semibold">
                Completion Date
                {formData.status === "COMPLETED" && <span className="text-danger ms-1">*</span>}
              </label>
              <input
                type="date"
                name="completionDate"
                className="form-control"
                value={formData.completionDate}
                onChange={handleChange}
                min={formData.serviceDate || undefined}
                disabled={formData.status !== "COMPLETED" && formData.status !== "CANCELLED"}
              />
              {formData.status !== "COMPLETED" && formData.status !== "CANCELLED" && (
                <div className="text-muted mt-1" style={{ fontSize: 11 }}>
                  Available when status is Completed or Cancelled
                </div>
              )}
            </div>

            <div className="col-md-6">
              <label className="form-label fw-semibold">Remarks / Closure Notes</label>
              <textarea
                name="remarks"
                className="form-control"
                rows={3}
                value={formData.remarks}
                onChange={handleChange}
                placeholder="Add any closure notes, follow-up actions, or remarks…"
              />
            </div>
          </Section>

          {/* ── System Information Tabs ── */}
          <ul className="nav nav-tabs mb-0 fw-semibold mt-4" style={{ backgroundColor: navbarColor }}>
            {[["systemNotes", "System Notes"]].map(([key, label]) => (
              <li className="nav-item" key={key}>
                <button
                  type="button"
                  className={`nav-link ${activeTab === key ? "active" : ""}`}
                  onClick={() => {
                    setActiveTab(key);
                    setVisitedTabs(prev => new Set(prev).add(key));
                  }}
                  style={{
                    color: activeTab === key ? navbarColor : "#fff",
                    backgroundColor: activeTab === key ? "#fff" : "transparent",
                    fontWeight: activeTab === key ? "600" : "normal",
                  }}
                >
                  {label}
                </button>
              </li>
            ))}
          </ul>
          <div className="border p-3 bg-white mb-4">
            <div className="text-muted small mb-2">
              <strong>Date Created:</strong>{" "}
              {originalRecord?.createdAt ? new Date(originalRecord.createdAt).toLocaleString() : "—"}
            </div>
            {visitedTabs.has("systemNotes") && (
              <div style={{ display: activeTab === "systemNotes" ? "block" : "none" }}>
                <SystemNotes entityType="MAINTENANCE" entityId={id} token={token} />
              </div>
            )}
          </div>

          {/* ── Action Buttons ── */}
          <div className="d-flex gap-2 mt-3 mb-5 align-items-center">
            <button className="btn btn-primary" type="submit" disabled={submitting}
              style={{ minWidth: 50 }}>
              {submitting
                ? <><span className="spinner-border spinner-border-sm me-1" /> Saving…</>
                : <> Save </>}
            </button>
            <button className="btn btn-secondary btn-sm" type="button" onClick={() => navigate(-1)}>
              Cancel
            </button>
            <button className="btn btn-outline-secondary btn-sm ms-auto" type="button"
              onClick={() => navigate(`/viewmaintenance/${id}`)}>
              <i className="bi bi-eye me-1"></i> View Record
            </button>
          </div>
        </form>
      </div>
    </>
  );
}
